/**
 * BM Versioning MVI Effects
 *
 * Architecture: MVI Pattern - Effects Layer
 * All side effects (API calls, localStorage, etc.) are isolated here.
 */

import type { TFeatureId } from "@/shared/types/feature";
import type { ICommitRequest, ICheckoutRequest } from "@/shared/types/bm-versioning";
import { getBmVersioningApiClient, toApiError } from "@/shared/api";
import { versioningIntents, type TVersioningIntent } from "./intents";

type Dispatch = (intent: TVersioningIntent) => void;

// ============================================================================
// API Client
// ============================================================================

const api = getBmVersioningApiClient();

// ============================================================================
// Effects
// ============================================================================

/**
 * Load commit history for a feature
 *
 * @returns true, если история загружена; false — при отказе
 */
export async function loadHistory(
  dispatch: Dispatch,
  featureId: TFeatureId
): Promise<boolean> {
  dispatch(versioningIntents.loadHistoryStart());

  try {
    const history = await api.getHistory(featureId);
    dispatch(versioningIntents.loadHistorySuccess(history));
    return true;
  } catch (error) {
    // Отказ не теряется молча: причина, присланная продуктом, доходит до
    // состояния как есть, а исход — до вызвавшего, чтобы тот показал её
    // (api-errors-R09, R08; BUG-0075).
    dispatch(versioningIntents.loadHistoryError(toApiError(error)));
    return false;
  }
}

/**
 * Create a commit from the current HEAD feature
 *
 * @returns The new feature ID (new HEAD), or null on error
 */
export async function commit(
  dispatch: Dispatch,
  featureId: TFeatureId,
  message?: string
): Promise<TFeatureId | null> {
  dispatch(versioningIntents.commitStart());

  try {
    const request: ICommitRequest = { message };
    const response = await api.commit(featureId, request);

    dispatch(
      versioningIntents.commitSuccess(response.newFeatureId, response.commitInfo)
    );

    return response.newFeatureId;
  } catch (error) {
    // Причина отказа — признак и параметры, присланные продуктом, — доходит
    // до состояния как есть: по ней диалог коммита показывает, что именно
    // произошло (business-model/versioning.md, api-errors-R08; BUG-0073).
    dispatch(versioningIntents.commitError(toApiError(error)));
    return null;
  }
}

/**
 * Checkout (create branch) from a specific commit
 *
 * @returns The new feature ID (branch), or null on error
 */
export async function checkout(
  dispatch: Dispatch,
  commitFeatureId: TFeatureId,
  branchName?: string
): Promise<TFeatureId | null> {
  dispatch(versioningIntents.checkoutStart(commitFeatureId));

  try {
    const request: ICheckoutRequest = { branchName };
    const response = await api.checkout(commitFeatureId, request);

    dispatch(
      versioningIntents.checkoutSuccess(response.newFeatureId, response.newFeatureName)
    );

    return response.newFeatureId;
  } catch (error) {
    // Причина отказа — признак и параметры, присланные продуктом, — доходит
    // до состояния как есть: по ней пользователь видит, что именно произошло
    // (business-model/versioning.md, api-errors-R08; BUG-0071).
    dispatch(versioningIntents.checkoutError(toApiError(error)));
    return null;
  }
}
